import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, Square, MapPin, Clock, Flame, Route as RouteIcon, Trophy, Footprints, ShieldAlert, Music2, CheckCircle2, Loader2, Camera, Medal, Zap, Gauge, Crown, Award, Share2, Lock, SkipBack, SkipForward } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { raceApi, apiError } from "../lib/api";

const BADGE_ICONS = { Footprints, Flame, Zap, Crown, Medal, Route: RouteIcon, Trophy };

const haversine = (a, b) => {
  const R = 6371000, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const fmtTime = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h > 0 ? `${h}:` : "") + `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};
const RACE_KEY = "supera_race_active";
const notifyRace = () => { try { window.dispatchEvent(new Event("supera-race-update")); } catch {} };

export default function RaceUser() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [view, setView] = useState("list"); // list | prep | tracking | summary
  const [mes, setMes] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [rankMonth, setRankMonth] = useState(1);
  const [myRuns, setMyRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [rankScope, setRankScope] = useState("mensual"); // mensual | anual
  const [totalRank, setTotalRank] = useState(null);
  const [feed, setFeed] = useState([]);

  // prep
  const [accepted, setAccepted] = useState(false);
  const [weight, setWeight] = useState("");
  const [playlist, setPlaylist] = useState(null);
  const [musicPlaylists, setMusicPlaylists] = useState([]);
  const [trackIdx, setTrackIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // tracking
  const [dist, setDist] = useState(0); // metros
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [reached, setReached] = useState(false);
  const [gpsError, setGpsError] = useState("");
  const [summary, setSummary] = useState(null);

  const watchId = useRef(null);
  const timerId = useRef(null);
  const lastPt = useRef(null);
  const route = useRef([]);
  const wakeLock = useRef(null);
  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const poly = useRef(null);
  const near100Warned = useRef(false);
  const lastKmSpoken = useRef(0);
  const accSec = useRef(0);      // segundos acumulados antes del segmento activo
  const segStart = useRef(null); // Date.now() ms al iniciar el segmento activo (corriendo)
  const hydrated = useRef(false);

  // Tiempo por reloj absoluto: sobrevive a segundo plano / recarga del navegador
  const computeElapsed = () => {
    let s = accSec.current;
    if (segStart.current) s += (Date.now() - segStart.current) / 1000;
    return Math.max(0, Math.floor(s));
  };

  const clearRace = () => { try { localStorage.removeItem(RACE_KEY); } catch {} notifyRace(); };


  const speak = (text) => {
    try {
      if (!("speechSynthesis" in window)) return;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "es-MX"; u.rate = 1; u.pitch = 1.05;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {}
  };

  const target = mes ? mes.km : 0;
  const tipo = cfg?.tipo || "carrera";
  const coeff = cfg?.cal_coeff || 0.8;
  const usedWeight = Number(weight) || cfg?.default_weight || 50;
  const calories = Math.round(coeff * usedWeight * (dist / 1000));
  const paceMin = dist > 0 ? (elapsed / 60) / (dist / 1000) : 0;

  // ---- Reproductor de música propia de Supera (continuo, sin cortes) ----
  const curTrack = playlist?.tracks?.[trackIdx] || null;
  const selectPlaylist = (pl) => {
    setPlaylist(pl); setTrackIdx(0); setIsPlaying(true);
    const a = audioRef.current;
    if (a && pl?.tracks?.length) {
      a.src = raceApi.musicUrl(pl.tracks[0].id); a.load();
      a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  };
  const togglePlay = () => {
    const a = audioRef.current; if (!a || !curTrack) return;
    if (a.paused) { if (!a.src) { a.src = raceApi.musicUrl(curTrack.id); a.load(); } a.play().then(() => setIsPlaying(true)).catch(() => {}); }
    else { a.pause(); setIsPlaying(false); }
  };
  const nextTrack = () => { if (playlist?.tracks?.length) { setIsPlaying(true); setTrackIdx((i) => (i + 1) % playlist.tracks.length); } };
  const prevTrack = () => { if (playlist?.tracks?.length) { setIsPlaying(true); setTrackIdx((i) => (i - 1 + playlist.tracks.length) % playlist.tracks.length); } };
  // Al cambiar de pista (auto-avance / anterior / siguiente) carga y sigue sonando.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !curTrack) return;
    const url = raceApi.musicUrl(curTrack.id);
    if (a.src !== url) { a.src = url; a.load(); }
    if (isPlaying) a.play().catch(() => {});
  }, [trackIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    raceApi.config().then((d) => { setCfg(d); }).catch((e) => toast.error(apiError(e)));
    raceApi.myRuns().then(setMyRuns).catch(() => {});
    raceApi.stats().then(setStats).catch(() => {});
    raceApi.feed().then((d) => setFeed(d.feed || [])).catch(() => {});
    raceApi.music().then((d) => setMusicPlaylists(d.playlists || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);
  useEffect(() => { if (view === "list" && rankScope === "mensual") raceApi.ranking(rankMonth).then((d) => setRanking(d.ranking)).catch(() => {}); }, [rankMonth, view, rankScope]);
  useEffect(() => { if (view === "list" && rankScope === "anual") raceApi.rankingTotal(0).then(setTotalRank).catch(() => {}); }, [view, rankScope]);

  // ---- Persistencia: guarda el estado de la carrera activa en cada cambio ----
  useEffect(() => {
    if (view !== "tracking" || !mes) return;
    try {
      const data = {
        mes, dist, accSec: accSec.current, segStart: segStart.current,
        running, reached, route: route.current.slice(-2000), weight, playlist, trackIdx,
        lastKmSpoken: lastKmSpoken.current, near100Warned: near100Warned.current,
        savedAt: Date.now(),
      };
      localStorage.setItem(RACE_KEY, JSON.stringify(data));
      notifyRace();
    } catch {}
  }, [view, mes, dist, running, reached, elapsed, weight, playlist, trackIdx]);

  // ---- Rehidratación al montar: reanuda la carrera si la app se recargó ----
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    let d;
    try { const raw = localStorage.getItem(RACE_KEY); d = raw ? JSON.parse(raw) : null; } catch { d = null; }
    if (!d || !d.mes) return;
    setMes(d.mes);
    setDist(d.dist || 0);
    setReached(!!d.reached);
    setWeight(d.weight || "");
    setPlaylist(d.playlist || null);
    setTrackIdx(d.trackIdx || 0);
    setIsPlaying(false); // el navegador bloquea autoplay tras recarga; el usuario toca ▶ para reanudar música
    route.current = Array.isArray(d.route) ? d.route : [];
    lastKmSpoken.current = d.lastKmSpoken || 0;
    near100Warned.current = !!d.near100Warned;
    accSec.current = d.accSec || 0;
    if (route.current.length) {
      const last = route.current[route.current.length - 1];
      lastPt.current = { lat: last[0], lng: last[1] };
    }
    const wasRunning = !!(d.running && d.segStart);
    segStart.current = wasRunning ? d.segStart : null;
    setElapsed(computeElapsed());
    setView("tracking");
    setTimeout(() => {
      initMap();
      if (poly.current && route.current.length) {
        poly.current.setLatLngs(route.current);
        if (mapRef.current) mapRef.current.setView(route.current[route.current.length - 1], 17);
      }
    }, 300);
    if (wasRunning) {
      (async () => { try { if ("wakeLock" in navigator) wakeLock.current = await navigator.wakeLock.request("screen"); } catch {} })();
      beginWatch(); beginTimer(); setRunning(true);
      toast("Reanudamos tu carrera automáticamente. Tu progreso está a salvo. 🏃");
    } else {
      setRunning(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // ---- Map ----
  const initMap = () => {
    if (mapRef.current || !mapEl.current) return;
    const m = L.map(mapEl.current, { zoomControl: false, attributionControl: false }).setView([19.4326, -99.1332], 16);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(m);
    poly.current = L.polyline([], { color: "#E9A400", weight: 6 }).addTo(m);
    mapRef.current = m;
  };
  const pushPoint = (lat, lng) => {
    route.current.push([lat, lng]);
    if (poly.current) poly.current.setLatLngs(route.current);
    if (mapRef.current) mapRef.current.setView([lat, lng], 17);
  };

  const cleanup = () => {
    if (watchId.current != null) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
    if (timerId.current) { clearInterval(timerId.current); timerId.current = null; }
    if (wakeLock.current) { try { wakeLock.current.release(); } catch {} wakeLock.current = null; }
  };
  useEffect(() => () => { cleanup(); if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  const startTracking = async () => {
    if (!navigator.geolocation) { setGpsError("Tu dispositivo no permite geolocalización."); return; }
    setView("tracking");
    setDist(0); setElapsed(0); setReached(false); setGpsError(""); near100Warned.current = false;
    route.current = []; lastPt.current = null; lastKmSpoken.current = 0;
    accSec.current = 0; segStart.current = Date.now();
    setTimeout(initMap, 200);
    try { if ("wakeLock" in navigator) wakeLock.current = await navigator.wakeLock.request("screen"); } catch {}
    beginWatch();
    beginTimer();
    setRunning(true);
    speak("¡Comienza tu reto Supera! Recuerda: el liderazgo también se entrena. ¡Vamos!");
  };

  const beginTimer = () => {
    timerId.current = setInterval(() => setElapsed(computeElapsed()), 1000);
  };
  const beginWatch = () => {
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (accuracy && accuracy > 40) return; // ignora lecturas imprecisas
        const pt = { lat: latitude, lng: longitude };
        pushPoint(latitude, longitude);
        if (lastPt.current) {
          const d = haversine(lastPt.current, pt);
          if (d > 1 && d < 80) setDist((prev) => prev + d);
        }
        lastPt.current = pt;
      },
      (err) => setGpsError("No pudimos acceder a tu ubicación (" + err.message + "). Activa el GPS y los permisos."),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
    );
  };

  // avisos de distancia + voz motivante por km
  useEffect(() => {
    const tkm = target * 1000;
    const km = Math.floor(dist / 1000);
    if (km >= 1 && km > lastKmSpoken.current) {
      lastKmSpoken.current = km;
      speak(`Llevas ${km} ${km === 1 ? "kilómetro" : "kilómetros"}. ¡Vas bien, líder Supera! No te rindas.`);
    }
    if (!reached && tkm > 0 && dist >= tkm - 100 && dist < tkm && !near100Warned.current) {
      near100Warned.current = true;
      toast("¡Ya casi! Te faltan menos de 100 metros. 💪", { icon: "🏁" });
      speak("¡Ya casi! Te faltan menos de cien metros. ¡Termina fuerte, líder Supera!");
    }
    if (!reached && tkm > 0 && dist >= tkm) {
      setReached(true);
      toast.success("¡Distancia del reto cumplida! Puedes finalizar o seguir.");
      speak("¡Reto cumplido! Lo lograste, líder Supera. Puedes finalizar o seguir sumando.");
    }
  }, [dist, target, reached]);

  const pause = () => {
    if (segStart.current) { accSec.current += (Date.now() - segStart.current) / 1000; segStart.current = null; }
    cleanup(); setRunning(false); setElapsed(computeElapsed());
  };
  const resume = async () => {
    if (reached) toast("Tu carrera de este mes ya fue cumplida. Puedes seguir sumando distancia. 🏃");
    try { if ("wakeLock" in navigator) wakeLock.current = await navigator.wakeLock.request("screen"); } catch {}
    segStart.current = Date.now();
    beginWatch(); beginTimer(); setRunning(true);
  };

  const finish = async () => {
    cleanup();
    if (segStart.current) { accSec.current += (Date.now() - segStart.current) / 1000; segStart.current = null; }
    setRunning(false);
    const duration = Math.max(0, Math.floor(accSec.current));
    const payload = {
      mes: mes.mes, distance_km: dist / 1000, duration_sec: duration, calories,
      weight: Number(weight) || null, route: route.current.slice(0, 2000),
    };
    try {
      const r = await raceApi.saveRun(payload);
      clearRace();
      setSummary(r);
      setView("summary");
      raceApi.myRuns().then(setMyRuns).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPrep = (m) => { setMes(m); setAccepted(false); setWeight(""); setPlaylist(musicPlaylists[0] || null); setTrackIdx(0); setIsPlaying(false); setView("prep"); };
  const backToList = () => { setView("list"); setMes(null); setSummary(null); load(); };
  const headerBack = () => {
    if (view === "tracking") { navigate("/superax"); return; } // minimizar: la carrera sigue viva
    if (view === "list") { navigate("/superax"); return; }
    backToList();
  };
  const abandonRace = () => {
    if (!window.confirm("¿Seguro que quieres descartar esta carrera? Perderás el progreso no guardado.")) return;
    cleanup(); clearRace(); setRunning(false);
    route.current = []; lastPt.current = null; accSec.current = 0; segStart.current = null;
    setDist(0); setElapsed(0); setReached(false);
    setView("list"); setMes(null); setSummary(null); load();
    toast("Carrera descartada.");
  };

  const shareAchievement = async () => {
    const r = summary.run;
    const c = document.createElement("canvas");
    c.width = 1080; c.height = 1080;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 1080, 1080);
    g.addColorStop(0, "#0A1C30"); g.addColorStop(1, "#1c3e5f");
    ctx.fillStyle = g; ctx.fillRect(0, 0, 1080, 1080);
    ctx.fillStyle = "#E9A400"; ctx.fillRect(0, 0, 1080, 14);
    ctx.textAlign = "center";
    ctx.fillStyle = "#E9A400"; ctx.font = "bold 46px Arial";
    ctx.fillText("SUPERA RUN", 540, 150);
    ctx.fillStyle = "#ffffff"; ctx.font = "bold 40px Arial";
    ctx.fillText(`${tipo === "caminata" ? "Caminata" : "Carrera"} · Mes ${r.mes}`, 540, 230);
    ctx.fillStyle = "#E9A400"; ctx.font = "bold 220px Arial";
    ctx.fillText(r.distance_km.toFixed(2), 540, 560);
    ctx.fillStyle = "#ffffff"; ctx.font = "bold 60px Arial";
    ctx.fillText("KILÓMETROS", 540, 640);
    ctx.font = "40px Arial"; ctx.fillStyle = "#cbd5e1";
    ctx.fillText(`⏱ ${fmtTime(r.duration_sec)}    🔥 ${r.calories} kcal    ⚡ ${r.pace} min/km`, 540, 740);
    ctx.fillStyle = "#E9A400"; ctx.font = "bold 54px Arial";
    ctx.fillText("¡SOY LÍDER SUPERA!", 540, 880);
    ctx.fillStyle = "#94a3b8"; ctx.font = "32px Arial";
    ctx.fillText("liderazgosupera.app", 540, 980);
    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    const file = new File([blob], "supera-run.png", { type: "image/png" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mi carrera Supera", text: "¡Completé mi reto Supera Run! 🏃 #LíderSupera" });
        return;
      }
    } catch {}
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "supera-run.png"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Imagen descargada para compartir");
  };

  if (!cfg) return <div className="min-h-screen bg-cream grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[#E9A400]" /></div>;

  const actLabel = tipo === "caminata" ? "caminata" : "carrera";
  const ActIcon = tipo === "caminata" ? Footprints : Zap;

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="race-user">
      <header className="bg-navy sticky top-0 z-20" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-3xl mx-auto px-4 h-14 sm:h-16 flex items-center justify-between">
          <button data-testid="race-back" onClick={headerBack} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold">
            <ArrowLeft className="w-5 h-5" /> {view === "list" ? "Supera X" : view === "tracking" ? "Minimizar" : "Volver"}
          </button>
          <span className="text-white font-extrabold tracking-wide text-sm sm:text-base">SUPERA <span className="text-[#E9A400]">RUN</span></span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}>
        {/* Reproductor de música Supera persistente (sigue sonando de preparación a carrera, sin cortes) */}
        {playlist && (view === "prep" || view === "tracking") && (
          <div data-testid="race-player" data-no-i18n className="mb-4 rounded-2xl bg-white ring-1 ring-black/5 p-3">
            <audio ref={audioRef} onEnded={nextTrack} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} preload="auto" />
            <div className="flex items-center gap-3">
              <button data-testid="race-music-toggle" onClick={togglePlay} className="w-11 h-11 rounded-full bg-[#0A1C30] text-white grid place-items-center flex-shrink-0 active:scale-95 transition-transform">
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold text-[#E9A400] flex items-center gap-1"><Music2 className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{playlist.label} · Música Supera</span></div>
                <div className="text-sm font-bold text-[#0A1C30] truncate">{curTrack?.title || "—"}</div>
              </div>
              <button data-testid="race-music-prev" onClick={prevTrack} className="w-9 h-9 grid place-items-center text-[#0A1C30] hover:text-[#E9A400]"><SkipBack className="w-5 h-5" /></button>
              <button data-testid="race-music-next" onClick={nextTrack} className="w-9 h-9 grid place-items-center text-[#0A1C30] hover:text-[#E9A400]"><SkipForward className="w-5 h-5" /></button>
            </div>
          </div>
        )}
        {/* ---------- LIST ---------- */}
        {view === "list" && (
          <div data-testid="race-list">
            <div className="rounded-3xl bg-gradient-to-br from-[#0A1C30] via-[#12314f] to-[#1c3e5f] p-6 text-white relative overflow-hidden">
              <span className="absolute -top-10 -right-8 w-40 h-40 rounded-full bg-[#E9A400]/20 blur-3xl" />
              <div className="relative">
                <div className="text-[11px] font-bold tracking-widest text-[#E9A400]">SUPERA X · {tipo === "caminata" ? "CAMINATA" : "CARRERA"} MENSUAL</div>
                <h1 className="text-3xl font-extrabold mt-1">{cfg.titulo}</h1>
                <p className="text-white/70 text-sm mt-2">{cfg.intro}</p>
              </div>
            </div>

            <h2 className="text-lg font-extrabold text-[#0A1C30] mt-6 mb-2 flex items-center gap-2"><ActIcon className="w-5 h-5 text-[#E9A400]" /> Elige tu mes</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {cfg.meses.map((m) => (
                <button key={m.mes} data-testid={`race-month-${m.mes}`} onClick={() => openPrep(m)} className="relative text-left rounded-2xl bg-white ring-1 ring-black/5 p-4 hover:shadow-md transition-all active:scale-[0.98]">
                  {m.completado && <span className="absolute top-2 right-2 text-emerald-500"><CheckCircle2 className="w-5 h-5" /></span>}
                  <div className="text-xs font-bold text-[#9AA3B2]">MES {m.mes}</div>
                  <div className="text-3xl font-extrabold text-[#0A1C30] mt-1">{m.km} <span className="text-base font-bold text-[#E9A400]">km</span></div>
                  <div className="text-[11px] text-[#6B7688] mt-0.5 capitalize">{actLabel}</div>
                </button>
              ))}
            </div>

            {/* Logros / Insignias */}
            {stats && (
              <div className="mt-8" data-testid="race-badges">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-extrabold text-[#0A1C30] flex items-center gap-2"><Award className="w-5 h-5 text-[#E9A400]" /> Mis logros</h2>
                  <span className="text-xs font-bold text-[#6B7688]">{stats.total_km} km totales · {stats.meses_completados}/12 meses</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-2">
                  {stats.badges.map((b) => {
                    const BIcon = BADGE_ICONS[b.icon] || Award;
                    return (
                      <div key={b.key} title={b.desc} data-testid={`race-badge-${b.key}`} className={`rounded-2xl p-3 text-center ring-1 ${b.earned ? "bg-[#E9A400]/15 ring-[#E9A400]/40" : "bg-black/[0.03] ring-black/5 opacity-50"}`}>
                        <div className="relative flex justify-center">
                          <BIcon className={`w-7 h-7 ${b.earned ? "text-[#E9A400]" : "text-[#9AA3B2]"}`} />
                          {!b.earned && <Lock className="w-3 h-3 text-[#9AA3B2] absolute -right-1 -bottom-1" />}
                        </div>
                        <div className="text-[10px] font-bold text-[#0A1C30] mt-1 leading-tight">{b.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ranking */}
            <div className="mt-8" data-testid="race-ranking">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h2 className="text-lg font-extrabold text-[#0A1C30] flex items-center gap-2"><Trophy className="w-5 h-5 text-[#E9A400]" /> Ranking</h2>
                <div className="flex items-center gap-2">
                  <div className="flex bg-black/5 rounded-lg p-0.5">
                    {[["mensual", "Mensual"], ["anual", "Acumulado"]].map(([v, l]) => (
                      <button key={v} data-testid={`race-rankscope-${v}`} onClick={() => setRankScope(v)} className={`text-xs font-bold px-3 py-1 rounded-md ${rankScope === v ? "bg-white shadow text-[#0A1C30]" : "text-[#6B7688]"}`}>{l}</button>
                    ))}
                  </div>
                  {rankScope === "mensual" && (
                    <select data-testid="race-rank-month" value={rankMonth} onChange={(e) => setRankMonth(Number(e.target.value))} className="rounded-lg border border-black/10 px-2 py-1 text-sm font-bold">
                      {cfg.meses.map((m) => <option key={m.mes} value={m.mes}>Mes {m.mes} ({m.km}km)</option>)}
                    </select>
                  )}
                </div>
              </div>

              {rankScope === "mensual" ? (
                <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-hidden">
                  {ranking.length === 0 ? <p className="text-sm text-[#6B7688] p-5 text-center">Aún no hay participantes este mes. ¡Sé el primero! 🏁</p> : (
                    ranking.map((r) => (
                      <div key={r.posicion} className={`flex items-center gap-3 px-4 py-3 border-b border-black/5 ${r.es_yo ? "bg-[#E9A400]/10" : ""}`}>
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${r.posicion <= 3 ? "bg-[#E9A400] text-white" : "bg-black/5 text-[#6B7688]"}`}>{r.posicion}</span>
                        <span className="flex-1 text-sm font-bold text-[#0A1C30] truncate">{r.nombre}{r.es_yo && " (tú)"}{!r.completo && <span className="ml-1 text-[10px] text-[#b45309]">· incompleta</span>}</span>
                        <span className="text-xs text-[#6B7688] tabular-nums">{r.distance_km.toFixed(2)} km</span>
                        <span className="text-xs font-bold text-[#0A1C30] tabular-nums w-16 text-right">{fmtTime(r.duration_sec)}</span>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="race-ranking-anual">
                  <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-hidden">
                    <div className="px-4 py-2 text-xs font-extrabold text-[#6B7688] bg-black/[0.02]">POR USUARIO (km totales)</div>
                    {(!totalRank || totalRank.usuarios.length === 0) ? <p className="text-sm text-[#6B7688] p-5 text-center">Sin datos aún.</p> :
                      totalRank.usuarios.slice(0, 20).map((u) => (
                        <div key={u.posicion} className={`flex items-center gap-3 px-4 py-2.5 border-b border-black/5 ${u.es_yo ? "bg-[#E9A400]/10" : ""}`}>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-extrabold ${u.posicion <= 3 ? "bg-[#E9A400] text-white" : "bg-black/5 text-[#6B7688]"}`}>{u.posicion}</span>
                          <span className="flex-1 text-sm font-bold text-[#0A1C30] truncate">{u.nombre}{u.es_yo && " (tú)"}</span>
                          <span className="text-sm font-extrabold text-[#E9A400] tabular-nums">{u.total_km} km</span>
                        </div>
                      ))}
                  </div>
                  <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-hidden">
                    <div className="px-4 py-2 text-xs font-extrabold text-[#6B7688] bg-black/[0.02]">POR GRUPO (km totales)</div>
                    {(!totalRank || totalRank.grupos.length === 0) ? <p className="text-sm text-[#6B7688] p-5 text-center">Sin datos aún.</p> :
                      totalRank.grupos.map((gp, i) => (
                        <div key={gp.grupo} className="flex items-center gap-3 px-4 py-2.5 border-b border-black/5">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-extrabold ${i < 3 ? "bg-[#0A1C30] text-white" : "bg-black/5 text-[#6B7688]"}`}>{i + 1}</span>
                          <span className="flex-1 text-sm font-bold text-[#0A1C30] truncate">{gp.grupo} <span className="text-[10px] text-[#9AA3B2]">({gp.usuarios})</span></span>
                          <span className="text-sm font-extrabold text-[#E9A400] tabular-nums">{gp.total_km} km</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mis carreras */}
            {myRuns.length > 0 && (
              <div className="mt-8" data-testid="race-my-runs">
                <h2 className="text-lg font-extrabold text-[#0A1C30] mb-2 flex items-center gap-2"><Medal className="w-5 h-5 text-[#E9A400]" /> Mis {actLabel}s</h2>
                <div className="space-y-2">
                  {myRuns.map((r) => (
                    <div key={r.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-4 flex items-center gap-3">
                      <span className="w-10 h-10 rounded-xl bg-[#E9A400]/15 flex items-center justify-center text-[#E9A400] font-extrabold text-sm">M{r.mes}</span>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-[#0A1C30]">{r.distance_km.toFixed(2)} km · {fmtTime(r.duration_sec)}</div>
                        <div className="text-xs text-[#6B7688]">{r.calories} kcal · ritmo {r.pace} min/km {r.completo && <span className="text-emerald-600 font-bold">· completada ✓</span>}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Muro de logros del grupo */}
            {feed.length > 0 && (
              <div className="mt-8" data-testid="race-feed">
                <h2 className="text-lg font-extrabold text-[#0A1C30] mb-2 flex items-center gap-2"><Award className="w-5 h-5 text-[#E9A400]" /> Muro del grupo</h2>
                <div className="space-y-2">
                  {feed.map((f) => (
                    <div key={f.id} className="rounded-2xl bg-white ring-1 ring-black/5 p-3.5 flex items-start gap-3">
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${f.tipo === "badge" ? "bg-[#E9A400]/15 text-[#E9A400]" : "bg-[#1E6F5C]/15 text-[#1E6F5C]"}`}>
                        {f.tipo === "badge" ? <Medal className="w-5 h-5" /> : <Footprints className="w-5 h-5" />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-[#0A1C30] leading-snug"><b>{f.nombre}</b> {f.text}</p>
                        <span className="text-[11px] text-[#9AA3B2]">{new Date(f.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------- PREP ---------- */}
        {view === "prep" && mes && (
          <div data-testid="race-prep" className="space-y-5">
            <div className="rounded-3xl bg-[#0A1C30] p-6 text-white text-center">
              <div className="text-xs font-bold tracking-widest text-[#E9A400]">MES {mes.mes} · {actLabel.toUpperCase()}</div>
              <div className="text-5xl font-extrabold mt-1">{mes.km} km</div>
              <p className="text-white/60 text-sm mt-1">Prepárate para tu reto Supera de este mes.</p>
            </div>

            {/* Seguridad */}
            <div className="rounded-2xl bg-red-50 ring-1 ring-red-100 p-5">
              <div className="flex items-center gap-2 text-red-700 font-extrabold"><ShieldAlert className="w-5 h-5" /> Medidas de seguridad</div>
              <ul className="mt-3 space-y-2 text-sm text-[#4A5568]">
                {cfg.safety_warnings.map((s, i) => <li key={i} className="flex gap-2"><span className="text-red-500 font-bold">•</span> {s}</li>)}
              </ul>
              <label className="flex items-start gap-2 mt-4 cursor-pointer">
                <input data-testid="race-accept" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 w-5 h-5 accent-[#E9A400]" />
                <span className="text-sm font-bold text-[#0A1C30]">Acepto las condiciones de seguridad para mi salud, confirmo que un médico autorizó esta actividad y que iré acompañado/a por un adulto de confianza.</span>
              </label>
            </div>

            {/* Peso */}
            <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
              <div className="text-sm font-extrabold text-[#0A1C30]">Tu peso (opcional)</div>
              <p className="text-xs text-[#6B7688] mb-2">Para calcular tus calorías. Si lo dejas vacío usaremos una estimación estándar.</p>
              <div className="flex items-center gap-2">
                <input data-testid="race-weight" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder={String(cfg.default_weight)} className="w-28 rounded-xl border border-black/10 px-3 py-2 text-sm" />
                <span className="text-sm text-[#6B7688]">kg</span>
              </div>
            </div>

            {/* Música */}
            <div className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
              <div className="flex items-center gap-2 text-sm font-extrabold text-[#0A1C30]"><Music2 className="w-5 h-5 text-[#E9A400]" /> Música motivacional Supera</div>
              <p className="text-xs text-[#6B7688] mt-0.5 mb-3">Elige una lista y sonará <b>canción tras canción sin cortarse</b>, en bucle, durante toda tu {actLabel}. Puedes cambiar de lista cuando quieras. (Música libre de derechos, no necesitas cuenta de Spotify.)</p>
              {musicPlaylists.length === 0 ? (
                <p className="text-xs text-[#9AA3B2]">Preparando la música… vuelve a entrar en unos segundos.</p>
              ) : (
                <div className="flex flex-wrap gap-2 mb-1">
                  {musicPlaylists.map((p) => (
                    <button key={p.id} data-testid={`race-playlist-${p.id}`} onClick={() => selectPlaylist(p)} className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${playlist?.id === p.id ? "bg-[#E9A400] text-[#0A1C30]" : "bg-black/5 text-[#0A1C30] hover:bg-black/10"}`}>{p.label}</button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-[#9AA3B2] mt-2">El reproductor aparece arriba y seguirá sonando durante tu carrera. 🎵</p>
            </div>

            <button data-testid="race-start" disabled={!accepted} onClick={startTracking} className={`w-full py-4 rounded-2xl text-lg font-extrabold flex items-center justify-center gap-2 transition-all ${accepted ? "bg-[#E9A400] text-[#0A1C30] shadow-lg hover:shadow-xl active:scale-[0.98]" : "bg-black/10 text-[#9AA3B2] cursor-not-allowed"}`}>
              <Play className="w-6 h-6" /> COMENZAR YA
            </button>
            {!accepted && <p className="text-center text-xs text-[#b45309]">Debes aceptar las medidas de seguridad para comenzar.</p>}
          </div>
        )}

        {/* ---------- TRACKING ---------- */}
        {view === "tracking" && mes && (
          <div data-testid="race-tracking" className="space-y-4">
            <div className="rounded-2xl overflow-hidden ring-1 ring-black/10">
              <div ref={mapEl} data-testid="race-map" className="w-full h-56 bg-[#e8eef3]" />
            </div>
            {gpsError && <div className="rounded-xl bg-red-50 text-red-700 text-sm p-3">{gpsError}</div>}

            {/* Progreso */}
            <div className="rounded-2xl bg-[#0A1C30] p-5 text-white text-center">
              <div className="text-xs text-white/50 font-bold">DISTANCIA · reto {target} km</div>
              <div className="text-5xl sm:text-6xl font-extrabold tabular-nums mt-1">{(dist / 1000).toFixed(2)}<span className="text-2xl text-[#E9A400]"> km</span></div>
              <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#E9A400] transition-all" style={{ width: `${Math.min(100, (dist / 1000 / target) * 100)}%` }} />
              </div>
              {reached && <div className="mt-2 text-emerald-400 text-sm font-bold flex items-center justify-center gap-1"><CheckCircle2 className="w-4 h-4" /> ¡Reto cumplido!</div>}
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[[Clock, "Tiempo", fmtTime(elapsed)], [Flame, "Calorías", `${calories}`], [Gauge, "Ritmo", `${paceMin ? paceMin.toFixed(2) : "0.00"}`]].map(([Icon, label, val], i) => (
                <div key={i} className="rounded-2xl bg-white ring-1 ring-black/5 p-3 sm:p-4 text-center">
                  <Icon className="w-5 h-5 mx-auto text-[#E9A400]" />
                  <div className="text-lg sm:text-xl font-extrabold text-[#0A1C30] mt-1 tabular-nums">{val}</div>
                  <div className="text-[10px] sm:text-[11px] text-[#9AA3B2]">{label}{label === "Ritmo" && " min/km"}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 sticky bottom-0" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              {running ? (
                <button data-testid="race-pause" onClick={pause} className="flex-1 py-4 rounded-2xl font-extrabold bg-white ring-1 ring-black/10 text-[#0A1C30] flex items-center justify-center gap-2"><Pause className="w-5 h-5" /> Pausar</button>
              ) : (
                <button data-testid="race-resume" onClick={resume} className="flex-1 py-4 rounded-2xl font-extrabold bg-[#0A1C30] text-white flex items-center justify-center gap-2"><Play className="w-5 h-5" /> Reanudar</button>
              )}
              <button data-testid="race-finish" onClick={finish} className="flex-1 py-4 rounded-2xl font-extrabold bg-red-600 text-white flex items-center justify-center gap-2"><Square className="w-5 h-5" /> Finalizar</button>
            </div>
            <p className="text-center text-[11px] text-[#9AA3B2] -mt-1">
              Puedes cambiar de playlist o salir de la app: tu progreso se guarda solo. Toca “Minimizar” para volver luego.
            </p>
            <button data-testid="race-discard" onClick={abandonRace} className="w-full text-center text-xs font-semibold text-[#9AA3B2] hover:text-red-600 py-1">
              Descartar carrera
            </button>
          </div>
        )}

        {/* ---------- SUMMARY ---------- */}
        {view === "summary" && summary && (
          <div data-testid="race-summary" className="text-center space-y-4">
            <div className="rounded-3xl bg-gradient-to-br from-[#E9A400] to-[#c98a00] p-8 text-white">
              <Trophy className="w-14 h-14 mx-auto" />
              <h2 className="text-2xl font-extrabold mt-2">{summary.completo ? "¡Reto completado! 🎉" : "¡Buen esfuerzo! 💪"}</h2>
              <div className="text-5xl font-extrabold mt-3 tabular-nums">{summary.run.distance_km.toFixed(2)} km</div>
              <div className="text-white/90 mt-1">{fmtTime(summary.run.duration_sec)} · {summary.run.calories} kcal · ritmo {summary.run.pace} min/km</div>
              <div className="mt-4 inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 font-bold"><Medal className="w-4 h-4" /> Lugar #{summary.rank.posicion} de {summary.rank.total} este mes</div>
            </div>
            {summary.sospechosa && (
              <div className="rounded-2xl bg-orange-50 ring-1 ring-orange-200 p-4 text-left text-sm text-orange-800" data-testid="race-suspicious">
                ⚠️ Esta carrera fue marcada para revisión ({summary.motivo}) y no cuenta en el ranking. Asegúrate de mantener el GPS activo y correr de forma real.
              </div>
            )}
            {summary.nuevas_insignias?.length > 0 && (
              <div className="rounded-2xl bg-[#E9A400]/10 ring-1 ring-[#E9A400]/30 p-4" data-testid="race-new-badges">
                <div className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-2 justify-center"><Award className="w-5 h-5 text-[#E9A400]" /> ¡Nuevas insignias desbloqueadas!</div>
                <div className="flex justify-center gap-3 mt-2">
                  {summary.nuevas_insignias.map((b) => { const BIcon = BADGE_ICONS[b.icon] || Award; return (
                    <div key={b.key} className="text-center"><BIcon className="w-7 h-7 text-[#E9A400] mx-auto" /><div className="text-[10px] font-bold text-[#0A1C30] mt-1">{b.label}</div></div>
                  ); })}
                </div>
              </div>
            )}
            <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-5 text-left">
              <div className="flex items-center gap-2 text-amber-700 font-extrabold"><Camera className="w-5 h-5" /> ¡No olvides tu evidencia!</div>
              <p className="text-sm text-[#4A5568] mt-1">RECUERDA TOMAR UN SCREENSHOT DE ESTA PANTALLA para subirlo en tus evidencias de la Hoja de Compromisos.</p>
            </div>
            <button data-testid="race-share" onClick={shareAchievement} className="w-full py-3 rounded-2xl font-extrabold bg-[#1DB954] text-white flex items-center justify-center gap-2"><Share2 className="w-5 h-5" /> Compartir mi logro</button>
            <div className="flex gap-3">
              <button data-testid="race-summary-ranking" onClick={() => { setRankMonth(summary.run.mes); backToList(); }} className="flex-1 py-3 rounded-2xl font-extrabold bg-[#0A1C30] text-white flex items-center justify-center gap-2"><Trophy className="w-5 h-5" /> Ver ranking</button>
              <button onClick={() => navigate("/compromisos")} className="flex-1 py-3 rounded-2xl font-extrabold bg-white ring-1 ring-black/10 text-[#0A1C30] flex items-center justify-center gap-2"><Camera className="w-5 h-5" /> Subir evidencia</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
